
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ContentEditorProps {
  content: {
    id: string;
    key: string;
    content: string;
  };
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export const ContentEditor = ({ content, isOpen, onClose, onUpdate }: ContentEditorProps) => {
  const [editedContent, setEditedContent] = useState(content.content);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("edit");

  const getDisplayName = (key: string): string => {
    const displayNames: Record<string, string> = {
      home_title: '首页标题',
      home_subtitle: '首页副标题',
      home_description: '首页描述',
      form_title: '申请表单标题',
      lookup_title: '查询页面标题',
      stats_title: '统计数据标题',
      about_text: '关于我们内容',
      contact_info: '联系方式内容',
      footer_copyright: '版权信息',
      footer_links: '页脚链接'
    };
    return displayNames[key] || key;
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      console.log('Updating content:', content.key, editedContent);
      const { error } = await supabase
        .from('site_content')
        .update({ 
          content: editedContent,
          last_updated: new Date().toISOString()
        })
        .eq('id', content.id);

      if (error) throw error;

      toast.success('内容已更新');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating content:', error);
      toast.error('更新失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isJsonContent = content.key === 'footer_links';

  const validateAndFormatJson = () => {
    if (!isJsonContent) return true;
    
    try {
      JSON.parse(editedContent);
      return true;
    } catch (e) {
      toast.error('JSON格式无效，请检查格式');
      return false;
    }
  };

  const handleTabChange = (value: string) => {
    if (value === "preview" && isJsonContent) {
      if (!validateAndFormatJson()) {
        return;
      }
    }
    setActiveTab(value);
  };

  const renderPreview = () => {
    if (isJsonContent) {
      try {
        const parsedJson = JSON.parse(editedContent);
        return (
          <div className="bg-white/10 p-4 rounded-lg">
            <h3 className="text-white font-medium mb-2">页脚链接预览:</h3>
            <div className="flex flex-wrap gap-4">
              {parsedJson.map((link: any, index: number) => (
                <div key={index} className="bg-white/20 p-2 rounded">
                  <div className="text-sm text-white">{link.title} → {link.path}</div>
                </div>
              ))}
            </div>
          </div>
        );
      } catch (e) {
        return <div className="text-red-500">JSON格式无效</div>;
      }
    }
    
    return (
      <div className="bg-white/10 p-4 rounded-lg">
        <div className="text-white whitespace-pre-wrap">{editedContent}</div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] bg-gray-900 border-gray-800">
        <DialogHeader>
          <DialogTitle className="text-white">编辑内容: {getDisplayName(content.key)}</DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="edit">编辑</TabsTrigger>
            <TabsTrigger value="preview">预览</TabsTrigger>
          </TabsList>
          
          <TabsContent value="edit" className="space-y-4 pt-4">
            {isJsonContent && (
              <div className="text-yellow-400 text-sm bg-yellow-500/10 p-2 rounded mb-2">
                此内容为JSON格式，请确保格式正确。例如: 
                <pre className="text-xs mt-1 overflow-x-auto">
                  {`[
  { "title": "首页", "path": "/" },
  { "title": "关于我们", "path": "/about" }
]`}
                </pre>
              </div>
            )}
            
            <Textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="min-h-[250px] bg-white/10 text-white border-white/20"
              placeholder={`请输入${getDisplayName(content.key)}...`}
            />
          </TabsContent>
          
          <TabsContent value="preview" className="pt-4">
            {renderPreview()}
          </TabsContent>
        </Tabs>
        
        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={onClose} className="border-gray-700 text-gray-300 hover:bg-gray-800">
            取消
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isSubmitting}
            className="bg-gradient-to-r from-blue-500 to-purple-600 hover:opacity-90"
          >
            {isSubmitting ? '保存中...' : '保存'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
